<template>
  <!-- Kliendid in the hooldusplaat look (Seaded -> Kliendid uues kujunduses),
       agreed on a mockup first: the types with counts, three numbers that are
       also filters, one search with chips and an order, and on each row the
       contact, the objects, the last work order and unpaid work. -->
  <div class="cl">
    <div class="cl-head">
      <div>
        <div class="cl-cap">Kliendid</div>
        <h1 class="cl-title">{{ titles[type] }}</h1>
      </div>
      <div class="d-flex flex-wrap align-center ga-3">
        <!-- The office's; a technician has Kõik kliendid and nothing else. -->
        <div v-if="office" class="cl-seg" role="group" aria-label="Kliendi tüüp">
          <button
            v-for="option in typeOptions"
            :key="option.value"
            type="button"
            :class="{ on: type === option.value }"
            @click="setType(option.value)"
          >{{ option.text }}<small v-if="summary">{{ number(summary[option.count]) }}</small></button>
        </div>
        <v-btn v-if="office" color="green" variant="flat" :to="{ name: 'client/create' }">+ Lisa klient</v-btn>
      </div>
    </div>

    <div class="cl-strip">
      <button
        v-for="tile in tiles"
        :key="tile.key"
        type="button"
        class="cl-stat"
        :class="[tile.tone, { on: chips[tile.key] }]"
        @click="toggleChip(tile.key)"
      >
        <div class="cl-k">{{ tile.text }}</div>
        <div class="cl-v">{{ summary ? number(summary[tile.key]) : '…' }}</div>
        <div class="cl-s">{{ tile.sub }}</div>
      </button>
    </div>

    <div class="cl-filters">
      <label class="cl-search">
        <v-icon size="20">mdi-magnify</v-icon>
        <input v-model="q" type="search" placeholder="Nimi, telefon, e-post või aadress" aria-label="Otsi">
      </label>
      <div class="cl-chips">
        <button
          v-for="chip in chipOptions"
          :key="chip.key"
          type="button"
          class="cl-chip"
          :class="{ on: chips[chip.key] }"
          @click="toggleChip(chip.key)"
        >{{ chip.text }}</button>
      </div>
      <label class="cl-sort">Järjestus
        <select v-model="sort" aria-label="Järjestus">
          <option v-for="option in sortOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
        </select>
      </label>
    </div>

    <v-progress-linear :active="loading" indeterminate color="green" height="3" />
    <div class="cl-table" role="table" aria-label="Kliendid">
      <div class="cl-thead" role="row">
        <div>Klient</div><div>Kontakt</div><div>Objektid</div><div>Viimane töökäsk</div><div>Maksmata</div><div />
      </div>
      <div v-for="client in clients" :key="client.id" class="cl-row" :class="{ 'cl-row--prob': client.problematic }" role="row">
        <div class="cl-name">
          <span class="cl-kind" :class="{ 'cl-kind--co': client.type === types.company }" :title="client.type === types.company ? 'Firma' : 'Eraklient'">
            <v-icon size="18">{{ client.type === types.company ? 'mdi-domain' : 'mdi-account' }}</v-icon>
          </span>
          <div class="cl-min">
            <router-link :to="{ name: 'client/view/info', params: { clientId: client.id } }" class="cl-link">{{ client.fullName || '—' }}</router-link>
            <span v-if="client.problematic" class="cl-pill cl-pill--red">Probleemne</span>
            <div v-if="client.problematic && client.problematicNote" class="cl-reason" :title="client.problematicNote">{{ client.problematicNote }}</div>
            <div v-if="client.stats && client.stats.added" class="cl-sub">lisatud {{ day(client.stats.added) }}</div>
          </div>
        </div>
        <div class="cl-contact">
          <div>{{ client.phone || '' }}<span v-if="!client.phone" class="cl-miss">telefon puudub</span></div>
          <div class="cl-sub">{{ client.email || '' }}<span v-if="!client.email" class="cl-miss">e-post puudub</span></div>
        </div>
        <div class="cl-min">
          <template v-if="client.stats && client.stats.objects">
            <div class="cl-ellipsis">{{ [client.stats.address, client.stats.city].filter(Boolean).join(', ') }}</div>
            <div class="cl-sub">
              {{ client.stats.objects }} {{ client.stats.objects === 1 ? 'objekt' : 'objekti' }}<template v-if="client.stats.contracts"> · <span class="cl-blue">{{ client.stats.contracts }} lepinguline</span></template>
            </div>
          </template>
          <span v-else class="cl-sub">Objekti pole</span>
        </div>
        <div class="cl-work">
          <template v-if="client.stats && client.stats.lastWork">
            <span v-if="client.stats.lastTechnic" class="cl-ini" :style="initialsStyle(client.stats.lastTechnic.color)" :title="client.stats.lastTechnic.fullName">{{ initialsOf(client.stats.lastTechnic.fullName) }}</span>
            <div>
              <div>{{ day(client.stats.lastWork) }}</div>
              <div class="cl-sub">{{ client.stats.works }} {{ client.stats.works === 1 ? 'töökäsk' : 'töökäsku' }} kokku</div>
            </div>
          </template>
          <span v-else class="cl-sub">Töökäske pole</span>
        </div>
        <div>
          <span v-if="client.stats && client.stats.unpaid" class="cl-pill cl-pill--amber">{{ client.stats.unpaid }} maksmata</span>
          <span v-else class="cl-sub">—</span>
        </div>
        <div class="cl-acts">
          <router-link v-if="office" :to="{ name: 'client/edit', params: { clientId: client.id } }" class="cl-link cl-link--plain">Muuda</router-link>
        </div>
      </div>
      <div v-if="!loading && !clients.length" class="cl-empty">Nende filtritega kliente pole. Tühjenda mõni filter.</div>
    </div>

    <div class="cl-pager">
      <span>{{ rangeText }}</span>
      <!-- Seven page buttons do not fit a phone. -->
      <v-pagination v-if="pages > 1" v-model="page" :length="pages" :total-visible="$vuetify.display.smAndDown ? 3 : 7" :density="$vuetify.display.smAndDown ? 'compact' : 'comfortable'" />
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { mapActions, mapGetters } from 'vuex'
import dayjs from 'dayjs'
import { initials } from '@/components/frame/frameLinks'

const types = { private: 1, company: 2 }
const PER_PAGE = 25
const CHIPS = ['problematic', 'contract', 'unpaid', 'noObjects']

export default {
  data () {
    // What the address bar holds, so Back returns to the same list.
    const query = this.$route.query
    return {
      types,
      clients: [],
      count: 0,
      summary: null,
      loading: false,
      q: query.q || '',
      chips: Object.fromEntries(CHIPS.map(key => [key, query[key] === 'true'])),
      sort: ['added', 'name', 'work', 'unpaid'].includes(query.sort) ? query.sort : 'added',
      page: parseInt(query.page, 10) || 1,
      searchTimer: null,
      requestNo: 0,
      titles: { all: 'Kõik kliendid', private: 'Erakliendid', company: 'Firmad' },
      typeOptions: [
        { value: 'all', text: 'Kõik', count: 'all' },
        { value: 'private', text: 'Erakliendid', count: 'private' },
        { value: 'company', text: 'Firmad', count: 'company' }
      ],
      tiles: [
        { key: 'unpaid', text: 'Maksmata töid', sub: 'klienti, lõppenud tööd', tone: 'cl-stat--warn' },
        { key: 'problematic', text: 'Probleemsed', sub: 'märgitud probleemseks', tone: 'cl-stat--bad' },
        { key: 'noObjects', text: 'Objektita', sub: 'ühtegi objekti pole', tone: '' }
      ],
      chipOptions: [
        { key: 'problematic', text: 'Probleemsed' },
        { key: 'contract', text: 'Lepinguga' },
        { key: 'unpaid', text: 'Maksmata töid' },
        { key: 'noObjects', text: 'Objektita' }
      ],
      sortOptions: [
        { value: 'added', text: 'Viimati lisatud' },
        { value: 'name', text: 'Nimi A–Ü' },
        { value: 'work', text: 'Viimane töö' },
        { value: 'unpaid', text: 'Maksmata enne' }
      ]
    }
  },
  computed: {
    ...mapGetters(['isAdmin', 'isSecretary']),
    // The office's: the type tabs, Lisa klient and Muuda. A technician has the
    // list to read (2026-10-14) and nothing else.
    office () {
      return this.isAdmin || this.isSecretary
    },
    type () {
      if (!this.office) return 'all'
      return types[this.$route.params.type] ? this.$route.params.type : 'all'
    },
    params () {
      return {
        stats: true,
        type: types[this.type],
        q: this.q.trim() || undefined,
        ...Object.fromEntries(CHIPS.filter(key => this.chips[key]).map(key => [key, true])),
        sort: this.sort,
        limit: PER_PAGE,
        offset: (this.page - 1) * PER_PAGE
      }
    },
    pages () {
      return Math.max(1, Math.ceil(this.count / PER_PAGE))
    },
    rangeText () {
      if (!this.count) return '0 klienti'
      const from = (this.page - 1) * PER_PAGE + 1
      return from + '–' + Math.min(this.count, from + this.clients.length - 1) + ' / ' + this.number(this.count)
    }
  },
  watch: {
    q () {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => { this.page = 1; this.load() }, 300)
    },
    sort () { this.page = 1; this.load() },
    page () { this.load() },
    type () { this.page = 1; this.load(); this.loadSummary() }
  },
  methods: {
    ...mapActions(['showNotification']),
    initialsOf: initials,
    initialsStyle (color) {
      if (!color) return { background: '#9AAAA4', color: '#fff' }
      const hex = color.replace('#', '')
      const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex
      const [r, g, b] = [0, 2, 4].map(i => parseInt(full.slice(i, i + 2), 16))
      return { background: color, color: (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? '#16322A' : '#fff' }
    },
    number (n) {
      return n == null ? '' : Number(n).toLocaleString('et-EE')
    },
    day (value) {
      return value ? dayjs(value).format('DD.MM.YYYY') : ''
    },
    setType (value) {
      if (value !== this.type) this.$router.push({ name: 'client/list', params: { type: value } })
    },
    toggleChip (key) {
      this.chips = { ...this.chips, [key]: !this.chips[key] }
      this.page = 1
      this.load()
    },
    // The filters into the address bar, without a new history entry each.
    syncQuery () {
      const query = {
        ...(this.q.trim() ? { q: this.q.trim() } : {}),
        ...Object.fromEntries(CHIPS.filter(key => this.chips[key]).map(key => [key, 'true'])),
        ...(this.sort !== 'added' ? { sort: this.sort } : {}),
        ...(this.page > 1 ? { page: String(this.page) } : {})
      }
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({ query }).catch(() => {})
      }
    },
    async load () {
      const no = ++this.requestNo
      this.loading = true
      this.syncQuery()
      try {
        const result = await api.getClients(Object.fromEntries(Object.entries(this.params).filter(([, v]) => v !== undefined)))
        if (no !== this.requestNo) return
        this.clients = result.clients
        this.count = result.count
      } catch (err) {
        if (no === this.requestNo) this.showNotification({ text: err.message, color: 'red' })
      } finally {
        if (no === this.requestNo) this.loading = false
      }
    },
    async loadSummary () {
      try {
        this.summary = await api.getClientSummary(types[this.type] ? { type: types[this.type] } : {})
      } catch (err) {
        this.summary = null
      }
    }
  },
  mounted () {
    this.load()
    this.loadSummary()
  },
  beforeUnmount () {
    clearTimeout(this.searchTimer)
  }
}
</script>

<style scoped>
.cl {
  --c-ground: #F4F6F5; --c-surface: #FFFFFF; --c-ink: #16322A; --c-ink-2: #3E504A; --c-ink-3: #5B6B66;
  --c-line: #C9D2CE; --c-line-soft: #E1E7E4; --c-green: #2E7D32; --c-green-dark: #1B5E20; --c-green-bg: #EAF3EA;
  --c-blue: #1F5FD1; --c-blue-bg: #EEF3FC; --c-amber: #8F5400; --c-amber-bg: #FFF4E0; --c-red: #B3261E; --c-red-bg: #FCEDEC;
  font-family: 'Archivo Variable', 'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: var(--c-ink);
}
.cl button, .cl input, .cl select { font: inherit; color: inherit; }
.cl-head { display: flex; flex-wrap: wrap; align-items: end; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
.cl-cap { font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: var(--c-ink-3); font-weight: 600; }
.cl-title { font-size: 30px; font-weight: 600; margin: 2px 0 0; letter-spacing: -.01em; line-height: 1.2; }
.cl-seg { display: inline-flex; border: 1px solid var(--c-line); border-radius: 8px; overflow: hidden; background: var(--c-surface); }
.cl-seg button { border: 0; background: transparent; padding: 7px 14px; cursor: pointer; font-weight: 500; color: var(--c-ink-2); font-variant-numeric: tabular-nums; }
.cl-seg button + button { border-left: 1px solid var(--c-line); }
.cl-seg button.on { background: var(--c-ink); color: #fff; }
.cl-seg small { opacity: .7; margin-left: 5px; }
.cl-strip { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin: 0 0 14px; }
.cl-stat { background: var(--c-surface); border: 1px solid var(--c-line-soft); border-radius: 10px; padding: 12px 14px; cursor: pointer; text-align: left; }
.cl-stat:hover { border-color: var(--c-line); }
.cl-stat.on { border-color: var(--c-green); box-shadow: inset 0 0 0 1px var(--c-green); }
.cl-k { color: var(--c-ink-3); font-size: 12px; letter-spacing: .03em; }
.cl-v { font-size: 24px; font-weight: 600; font-variant-numeric: tabular-nums; }
.cl-s { color: var(--c-ink-3); font-size: 12px; }
.cl-stat--warn .cl-v { color: var(--c-amber); }
.cl-stat--bad .cl-v { color: var(--c-red); }
.cl-filters { display: flex; flex-wrap: wrap; gap: 10px 14px; align-items: center; margin-bottom: 10px; }
.cl-search { flex: 1 1 320px; min-width: 0; display: flex; align-items: center; gap: 8px; border: 1px solid var(--c-line); border-radius: 8px; padding: 0 12px; background: var(--c-surface); color: var(--c-ink-3); }
.cl-search input { border: 0; outline: 0; background: transparent; padding: 9px 0; width: 100%; min-width: 0; color: var(--c-ink); }
.cl-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.cl-chip { border: 1px solid var(--c-line); background: var(--c-surface); border-radius: 999px; padding: 5px 12px; cursor: pointer; font-size: 13px; }
.cl-chip.on { background: var(--c-green-bg); border-color: var(--c-green); color: var(--c-green-dark); font-weight: 600; }
.cl-sort { display: inline-flex; align-items: center; gap: 6px; color: var(--c-ink-3); font-size: 13px; }
.cl-sort select { border: 1px solid var(--c-line); border-radius: 6px; padding: 5px 8px; background: var(--c-surface); color: var(--c-ink); appearance: auto; }
.cl-table { background: var(--c-surface); border: 1px solid var(--c-line-soft); border-radius: 10px; overflow: hidden; }
.cl-thead, .cl-row { display: grid; grid-template-columns: minmax(0, 1.5fr) minmax(0, 1.2fr) minmax(0, 1.4fr) minmax(0, 1.1fr) 110px 72px; gap: 14px; align-items: center; padding: 10px 16px; }
.cl-thead { font-size: 12px; font-weight: 600; color: var(--c-ink-3); letter-spacing: .03em; border-bottom: 1px solid var(--c-line); }
.cl-row { border-top: 1px solid var(--c-line-soft); }
.cl-thead + .cl-row { border-top: 0; }
.cl-row:hover { background: var(--c-ground); }
.cl-name { display: flex; gap: 10px; align-items: center; min-width: 0; }
.cl-min { min-width: 0; }
.cl-kind { flex: none; width: 30px; height: 30px; border-radius: 8px; display: grid; place-items: center; background: var(--c-line-soft); color: var(--c-ink-2); }
.cl-kind--co { background: var(--c-blue-bg); color: var(--c-blue); }
.cl-link { color: var(--c-blue); font-weight: 600; text-decoration: none; }
.cl-link:hover { text-decoration: underline; }
.cl-link--plain { font-weight: 500; }
.cl-row--prob .cl-name .cl-link { color: var(--c-red); }
.cl-reason { font-size: 13px; color: #5B3700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
.cl-sub { color: var(--c-ink-3); font-size: 12px; }
.cl-blue { color: var(--c-blue); }
.cl-pill { display: inline-block; font-size: 11px; font-weight: 700; letter-spacing: .03em; padding: 1px 7px; border-radius: 999px; }
.cl-name .cl-pill { margin-left: 6px; vertical-align: 1px; }
.cl-pill--red { background: var(--c-red-bg); color: var(--c-red); }
.cl-pill--amber { background: var(--c-amber-bg); color: var(--c-amber); }
.cl-contact { min-width: 0; font-variant-numeric: tabular-nums; }
.cl-contact > div, .cl-ellipsis { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cl-miss { color: var(--c-ink-3); font-style: italic; }
.cl-work { display: flex; gap: 8px; align-items: center; min-width: 0; font-variant-numeric: tabular-nums; }
.cl-ini { display: inline-grid; place-items: center; width: 22px; height: 22px; border-radius: 50%; font-size: 10px; font-weight: 700; flex: none; }
.cl-acts { text-align: right; }
.cl-empty { padding: 30px; text-align: center; color: var(--c-ink-3); }
.cl-pager { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 10px; margin-top: 12px; color: var(--c-ink-3); font-variant-numeric: tabular-nums; }
@media (max-width: 1100px) {
  .cl-thead { display: none; }
  .cl-row { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 8px 14px; align-items: start; }
  .cl-acts { text-align: left; }
}
@media (max-width: 760px) {
  .cl-strip { grid-template-columns: minmax(0, 1fr); }
  .cl-row { grid-template-columns: minmax(0, 1fr); }
}
</style>
